// Types for the upstream MCP proxy admin endpoints (symone-v2 Feature B).
// `Upstream` / `UpstreamWrite` follow the typed `McpUpstream` / `McpUpstreamWrite`
// schemas in gateway/openapi.yaml. The sync, review, OAuth and host responses are still
// `type: object` there; those field names follow gateway/api/mcp_upstreams.py +
// connectors/mcp_upstream/*. Credentials (sync_credential, OAuth client secrets) are
// write-only and never returned — none of these types carries one.

export type Transport = 'streamable_http' | 'sse'
export type AuthScheme = 'api_key' | 'bearer' | 'oauth2'

export interface UpstreamHost {
  host: string
  note: string | null
  added_by: string | null
  added_at: string | null
}

/** openapi.yaml `McpUpstream` (GET /admin/connectors/mcp, GET /admin/connectors/mcp/{slug}). */
export interface Upstream {
  slug: string
  url: string
  transport: Transport
  auth_scheme: AuthScheme
  api_key_header: string | null
  /** The served `[Category]` description prefix (free text, required at register). */
  category: string
  /** The connector's 1–3 tool-category slugs (`connectors.categories`); [] when unset. */
  categories: string[]
  connector_name: string
  display_name: string
  exposure: 'dark' | 'live'
  has_sync_credential: boolean
  protocol_version: string | null
  /** `{name, version}` from the last initialize (display only). */
  server_info: ({ name?: string | null; version?: string | null } & Record<string, unknown>) | null
  last_sync_at: string | null
  last_sync_error: string | null
  tos_url: string | null
  tos_note: string | null
  tos_reviewed_at: string | null
  tos_reviewed_by: string | null
  resync_suggested_at: string | null
  created_at?: string
  updated_at?: string
}

/**
 * openapi.yaml `McpUpstreamWrite` (POST /admin/connectors/mcp, PATCH …/{slug}): the
 * upstream plus the write's side information.
 */
export interface UpstreamWrite extends Upstream {
  /** Register only: the OpenAPI connector this upstream replaced (D21), else null. */
  switched_from?: string | null
  accounts_marked_needs_reconnect?: number
  [extra: string]: unknown
}

export interface ToolSnapshot {
  description?: string | null
  title?: string | null
  input_schema?: unknown
  annotations?: unknown
}

export type ReviewState = 'pending' | 'changed' | 'approved' | 'removed'

export interface ReviewTool {
  name: string
  upstream_name: string | null
  state: ReviewState
  active: boolean
  approved: ToolSnapshot | null
  observed: ToolSnapshot
  diff: { new?: boolean; description?: boolean; title?: boolean; input_schema?: boolean; annotations?: boolean }
  flags: string[]
  rejected: boolean
  sync_error: string | null
  description_override: string | null
  annotations_override: unknown
  approved_at: string | null
  observed_at: string | null
}

export interface ReviewListing {
  slug: string
  tools: ReviewTool[]
}

export interface SyncSummary {
  slug: string
  connector_name: string
  protocol_version: string | null
  tool_count: number
  new: string[]
  changed: string[]
  suspended: string[]
  removed: string[]
  unchanged: string[]
  errors: { upstream_name: string; error: string }[]
}

export interface ResyncAllResult {
  upstreams: {
    slug: string
    status: 'synced' | 'error' | 'skipped'
    reason?: string
    error?: string
    new?: string[]
    changed?: string[]
    suspended?: string[]
    removed?: string[]
    errors?: number
  }[]
  synced: number
  failed: number
  skipped: number
}

export interface OAuthMetadata {
  resource: string | null
  resource_metadata_url: string | null
  issuer: string | null
  authorization_endpoint: string | null
  token_endpoint: string | null
  registration_endpoint: string | null
  scopes: string[]
}
export type OAuthDiff = Record<string, { frozen: unknown; observed: unknown }>

export interface OAuthRegistration extends OAuthMetadata {
  slug: string
  registration_method: 'dcr' | 'preregistered' | 'cimd' | null
  metadata_hash: string | null
  verified_at: string | null
  verified_by: string | null
  pending: {
    metadata: OAuthMetadata
    metadata_hash: string
    diff: OAuthDiff
    observed_at: string | null
    observed_by: string | null
  } | null
}

export interface OAuthVerifyResult {
  slug: string
  status: 'unchanged' | 'pending_approval'
  metadata_hash: string
  diff?: OAuthDiff
}

/** Human label for a review row: the D16 state plus whether the tool still serves. */
export function reviewStateLabel(t: Pick<ReviewTool, 'state' | 'active'>): {
  label: string
  badge: string
} {
  switch (t.state) {
    case 'pending':
      return { label: 'Pending (new)', badge: 'pending' }
    case 'changed':
      return t.active
        ? { label: 'Held: description change', badge: 'changed' }
        : { label: 'Suspended: schema change', badge: 'suspended' }
    case 'approved':
      return { label: 'Approved', badge: 'approved' }
    case 'removed':
      return { label: 'Removed upstream', badge: 'removed' }
    default:
      return { label: String(t.state), badge: 'pending' }
  }
}

export const UPSTREAMS_QUERY_KEY = ['admin-mcp-upstreams'] as const
export const HOSTS_QUERY_KEY = ['admin-mcp-upstream-hosts'] as const
export const UPSTREAM_DETAIL_PREFIX = ['admin-mcp-upstream'] as const
export const upstreamKey = (slug: string) => [...UPSTREAM_DETAIL_PREFIX, slug] as const
export const reviewKey = (slug: string) => ['admin-mcp-upstream-review', slug] as const
export const oauthKey = (slug: string) => ['admin-mcp-upstream-oauth', slug] as const

export function fmtTime(v: string | null | undefined): string {
  if (!v) return '—'
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? v : d.toISOString().replace('T', ' ').slice(0, 16) + 'Z'
}
